import { HttpHandlerFn, HttpRequest, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { ToastService } from '@shared/services/utils/toast.service';
import { catchError, tap, throwError } from 'rxjs';

export const toastInterceptor: HttpInterceptorFn = (
	req: HttpRequest<unknown>,
	next: HttpHandlerFn
) => {
	const toastService = inject(ToastService);

	// Store toast message from request headers
	const toastMessage = req.headers.get('X-Toast-Message');
	const toastType = req.headers.get('X-Toast-Type') ?? 'success';

	return next(req).pipe(
		tap((event) => {
			// Only process full response events
			if (!(event instanceof HttpResponse)) {
				return;
			}

			// Process header-based toast messages first
			if (toastMessage) {
				displayHeaderToast(toastService, toastMessage, toastType);
				return;
			}

			// Otherwise check response body for messages
			processResponseBody(event, toastService);
		}),
		catchError((error) => {
			if (error.error) {
				const errorMessage = extractErrorMessage(error);
				toastService.showError(errorMessage as string);
			}
			return throwError(() => error);
		})
	);
};

// Extract to smaller functions to reduce complexity

const displayHeaderToast = (toastService: ToastService, message: string, type: string): void => {
	switch (type) {
		case 'success':
			toastService.showSuccess(message);
			break;
		case 'error':
			toastService.showError(message);
			break;
		case 'warning':
			toastService.showWarning(message);
			break;
		default:
			toastService.showInfo(message);
	}
};

const processResponseBody = (response: HttpResponse<unknown>, toastService: ToastService): void => {
	if (!response.body || typeof response.body !== 'object') {
		return;
	}

	const body = response.body as any;

	// Check for success message
	if (body.message && body.status !== 'error') {
		toastService.showSuccess(body.message);
	}

	// Check for info messages
	if (body.info) {
		toastService.showInfo(body.info);
	}
};

const extractErrorMessage = (error: any): string | null => {
	if (error.status === 404) {
		return 'Resource not found';
	} else if (error.status === 403) {
		return 'You do not have permission to perform this action';
	} else if (error.status === 500) {
		return error.error?.message ?? error.message ?? 'Server error, please try again later';
	}
	return null;
};
